// src/components/kb/KnowledgeBase.tsx
//
// Knowledge Base list (Story 2.1): the org's KB corpus — title, source, brand
// tag, rep-shareable flag, lifecycle status, added/updated stamps — with an
// upload overlay and a routed detail (/kb/:documentId). New surface for the
// new corpus; the legacy /kb-updates screen is untouched. Any market-access
// user manages KB documents (FR-27, no admin gate). Plain full list at pilot
// corpus size; no optimistic updates — rows render only from the server.

import React, { useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Library, Upload } from 'lucide-react'
import clsx from 'clsx'
import { Button } from '../ui/Button'
import { useKbDocuments } from '../../hooks/useKbDocuments'
import { KbUploadForm } from './KbUploadForm'
import { formatDateTime } from '../../utils/dateUtils'
import type { YakKbDocument } from '../../types/yak'

// Lifecycle filter over the fetched list — client-side at pilot corpus size (no
// server round-trip). Default is Active (Bill's daily view = what Yak answers
// from); Retired and All are one click away so "nothing disappears" (decision 4).
type StatusFilter = 'active' | 'retired' | 'all'
const FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'retired', label: 'Retired' },
  { value: 'all', label: 'All' },
]

export function KnowledgeBase() {
  const { documents, loading, error, upload } = useKbDocuments()
  const navigate = useNavigate()
  const [showUpload, setShowUpload] = useState(false)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active')
  const uploadTriggerRef = useRef<HTMLButtonElement | null>(null)

  const filtered = useMemo(
    () =>
      statusFilter === 'all'
        ? documents
        : documents.filter(d => d.status === statusFilter),
    [documents, statusFilter]
  )

  const brandsInUse = useMemo(
    () =>
      Array.from(
        new Set(
          documents
            .map(d => d.brand)
            .filter((b): b is string => !!b && b.trim() !== '')
        )
      ).sort(),
    [documents]
  )

  const openUpload = (e: React.MouseEvent<HTMLButtonElement>) => {
    uploadTriggerRef.current = e.currentTarget
    setShowUpload(true)
  }

  const closeUpload = () => {
    setShowUpload(false)
    // Return focus to the trigger; fall back to <body> only if it unmounted.
    const trigger = uploadTriggerRef.current
    uploadTriggerRef.current = null
    if (trigger && document.contains(trigger)) trigger.focus()
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Knowledge Base</h1>
          <p className="text-sm text-gray-600 mt-1">
            {documents.length === 1
              ? '1 document.'
              : `${documents.length} documents.`}{' '}
            Yak answers from active documents only.
          </p>
        </div>
        {documents.length > 0 && (
          <Button onClick={openUpload}>
            <Upload className="w-4 h-4 mr-1" />
            Upload document
          </Button>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="card p-6">
          <div className="space-y-3" aria-hidden="true">
            {[0, 1, 2].map(i => (
              <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        </div>
      ) : documents.length === 0 ? (
        <div className="card p-10 text-center">
          <Library className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">
            Your knowledge base is empty. Upload the first document so Yak can
            answer from it.
          </p>
          <Button onClick={openUpload}>Upload the first document</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Lifecycle filter — labeled, keyboard-reachable buttons. */}
          <div
            className="inline-flex rounded-md border border-gray-200 p-0.5"
            role="group"
            aria-label="Filter by status"
          >
            {FILTERS.map(f => (
              <button
                key={f.value}
                type="button"
                aria-pressed={statusFilter === f.value}
                onClick={() => setStatusFilter(f.value)}
                className={clsx(
                  'px-3 py-1 text-sm rounded focus:outline-none focus:ring-2 focus:ring-brand-500',
                  statusFilter === f.value
                    ? 'bg-gray-100 text-gray-900 font-medium'
                    : 'text-gray-600 hover:text-gray-900'
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="card p-8 text-center text-sm text-gray-500">
              No {statusFilter} documents.
            </div>
          ) : (
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={THCLASS}>Title</th>
                      <th className={THCLASS}>Source</th>
                      <th className={THCLASS}>Brand</th>
                      <th className={THCLASS}>Sharing</th>
                      <th className={THCLASS}>Status</th>
                      <th className={THCLASS}>Added</th>
                      <th className={THCLASS}>Updated</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filtered.map(doc => (
                      <DocumentRow
                        key={doc.id}
                        doc={doc}
                        onOpen={() => navigate(`/kb/${doc.id}`)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {showUpload && (
        <KbUploadForm
          brandsInUse={brandsInUse}
          onUpload={upload}
          onClose={closeUpload}
        />
      )}
    </div>
  )
}

const THCLASS =
  'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'

function DocumentRow({
  doc,
  onOpen,
}: {
  doc: YakKbDocument
  onOpen: () => void
}) {
  return (
    <tr
      className="hover:bg-gray-50 transition-colors cursor-pointer"
      onClick={onOpen}
    >
      <td className="px-6 py-4 text-sm font-medium text-gray-900 max-w-xs truncate">
        {/* Title is a focusable link; the whole row is a convenience target. */}
        <Link
          to={`/kb/${doc.id}`}
          className="text-brand-700 hover:text-brand-800 hover:underline focus:outline-none focus:underline"
          onClick={e => e.stopPropagation()}
        >
          {doc.title}
        </Link>
      </td>
      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
        {doc.source}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {doc.brand ? doc.brand : <span className="text-gray-400">All</span>}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
        {/* Text label, never color/icon alone (accessibility floor). */}
        {doc.rep_shareable ? 'Shareable' : 'Internal'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {/* Retired = filled gray badge (the "done, quiet" hue); Active = quiet
            meta text. Both carry a text label — color is never the sole carrier. */}
        {doc.status === 'retired' ? (
          <span className="badge badge-gray">Retired</span>
        ) : (
          <span className="text-sm text-gray-500">Active</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {doc.created_at ? formatDateTime(doc.created_at) : '—'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {doc.updated_at ? formatDateTime(doc.updated_at) : '—'}
      </td>
    </tr>
  )
}
